import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import StatusBadge from "@/components/StatusBadge";
import QuestionnaireReglementaire from "@/components/QuestionnaireReglementaire";
import CoherencePanel from "@/components/CoherencePanel";
import SignatureCircuit from "@/components/SignatureCircuit";
import ParticipantsPanel from "@/components/ParticipantsPanel";
import PdpComplements from "@/components/PdpComplements";
import QualificationPanel from "@/components/QualificationPanel";
import DossierIncomplet from "@/components/DossierIncomplet";
import AnalyseRisques from "@/components/AnalyseRisques";
import DecisionIntervention from "@/components/DecisionIntervention";
import JournalPanel from "@/components/JournalPanel";
import PresenceBar from "@/components/PresenceBar";
import { useOperationSocket } from "@/hooks/useOperationSocket";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Printer, Download, RefreshCw, Plus, GraduationCap, FileDown, AlertTriangle } from "lucide-react";

const TABS = ["Synthèse", "Inspection commune", "Analyse des risques", "Plan de prévention", "Autorisations", "Actions", "Équipe", "Journal"];

export default function OperationDetail() {
  const { id } = useParams();
  const { labels, zones, zoneName, entreprises } = useApp();
  const { user } = useAuth();
  const nav = useNavigate();
  const [op, setOp] = useState(null);
  const [tab, setTab] = useState(0);
  const [pdp, setPdp] = useState(null);
  const [insp, setInsp] = useState(null);
  const [raw, setRaw] = useState({ participants: "", risques: "", mesures: "", documentsDemandes: "", reserves: "" });
  const [cohKey, setCohKey] = useState(0);
  const [cohBlocked, setCohBlocked] = useState(false);
  const [cohCount, setCohCount] = useState(0);
  const [journalKey, setJournalKey] = useState(0);
  const handleCohState = useCallback((c) => {
    setCohBlocked(!!c.bloque);
    setCohCount(c.nbBloquants ?? (c.checks || []).filter((x) => x.bloquant && !x.ok).length);
  }, []);

  const load = useCallback(() => {
    api.get(`/operations/${id}`).then((r) => {
      setOp(r.data);
      const io = r.data.inspection || { operationId: id, date: new Date().toISOString().slice(0, 10), participants: [], zonesVisitees: [], risques: [], mesures: [], documentsDemandes: [], reserves: [], validation: { valide: false, valideur: "", date: "" } };
      setInsp(io);
      setRaw({ participants: (io.participants || []).join("\n"), risques: (io.risques || []).join("\n"),
        mesures: (io.mesures || []).join("\n"), documentsDemandes: (io.documentsDemandes || []).join("\n"),
        reserves: (io.reserves || []).join("\n") });
    });
    api.get(`/operations/${id}/pdp`).then((r) => setPdp(r.data)).catch(() => {});
  }, [id]);
  useEffect(load, [load]);

  const onSocket = useCallback((msg) => {
    if (msg.type === "journal") {
      setJournalKey((k) => k + 1);
    } else if (msg.type === "invalidate") {
      if (msg.par && user && msg.par === user.name) return;
      toast.warning(`Cette section a été modifiée par ${msg.par || "un autre utilisateur"}. Rechargez la dernière version avant d'enregistrer.`,
        { duration: 8000, action: { label: "Recharger", onClick: () => load() } });
    } else if (msg.type === "notification") {
      if (user && (msg.destinataires || []).includes(user.id)) {
        window.dispatchEvent(new CustomEvent("permitco:notif"));
        toast.info(msg.contenu || "Nouvelle notification");
      }
    }
  }, [load, user]);
  const { presence, connected } = useOperationSocket(id, onSocket);
  const [reqAck, setReqAck] = useState(false);

  if (!op) return <div className="mono">Chargement…</div>;

  const saveInspection = async () => {
    const split = (s) => (s || "").split("\n");
    const payload = { ...insp, participants: split(raw.participants), risques: split(raw.risques),
      mesures: split(raw.mesures), documentsDemandes: split(raw.documentsDemandes), reserves: split(raw.reserves) };
    try { await api.post("/inspections", payload); toast.success("Inspection commune enregistrée"); load(); setCohKey((k) => k + 1); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const genPdp = async () => {
    try { const { data } = await api.post(`/operations/${id}/pdp`); setPdp(data); toast.success("Plan de prévention généré"); load(); setCohKey((k) => k + 1); }
    catch (e) {
      const d = e?.response?.data?.detail;
      const pts = d?.pointsBloquants;
      if (Array.isArray(pts) && pts.length) {
        toast.error(d.msg || "Génération impossible.", {
          description: pts.slice(0, 5).map((p) => `• ${p.libelle}`).join("\n"),
          duration: 8000,
        });
      } else { toast.error(apiErr(e)); }
      load(); setCohKey((k) => k + 1);
    }
  };
  const genActions = async () => {
    try { const { data } = await api.post(`/operations/${id}/generer-actions`); toast.success(`${data.created} action(s) générée(s)`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const siteZones = zones.filter((z) => z.siteId === op.siteId);
  const inspField = (k, v) => setInsp((s) => ({ ...s, [k]: v }));
  const generated = pdp && pdp.statutSignature !== "non_genere";
  const caps = op.mesCapacites || [];
  const can = (c) => caps.includes(c);
  const isFormation = op.typeOperation === "formation";

  return (
    <div>
      {isFormation && (
        <div className="notice" data-testid="formation-banner" style={{ background: "var(--ambre-p, #FDF3E0)", borderColor: "var(--ambre, #E49B1A)", display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
          <GraduationCap size={16} /> MODE FORMATION — DONNÉES SIMULÉES. Ce dossier n'a aucune valeur opérationnelle (signatures simulées, notifications désactivées).
        </div>
      )}
      <div className="crumb"><Link to="/operations">Opérations</Link> / {op.id}</div>
      <PresenceBar presence={presence} connected={connected} />
      {op.requalificationAlerte && !op.requalificationAlerte.acquittee && !reqAck && (
        <div className="notice" data-testid="requalif-banner" style={{ background: "#FDF3E0", borderColor: "#E49B1A", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <AlertTriangle size={16} color="#E49B1A" />
          <span style={{ flex: 1, fontSize: ".86rem" }}>
            <b>Régime réglementaire requalifié</b> suite à une modification : «&nbsp;{op.requalificationAlerte.deLabel}&nbsp;» → <b>«&nbsp;{op.requalificationAlerte.versLabel}&nbsp;»</b>. Vérifiez les impacts sur la suite de l'opération.
          </span>
          <button className="btn btn-g btn-sm" data-testid="requalif-ack"
            onClick={async () => { try { await api.post(`/operations/${id}/requalification/ack`); setReqAck(true); } catch (e) { /* noop */ } }}>
            J'ai pris connaissance
          </button>
        </div>
      )}
      <div className="page-head">
        <div>
          <h1 style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>{op.titre} <StatusBadge value={op.statut} label={labels.statutOp[op.statut]} />{isFormation && <span className="tag" data-testid="formation-badge" style={{ background: "var(--ambre, #E49B1A)", color: "#fff" }}>FORMATION</span>}</h1>
          <div style={{ color: "var(--text-muted)", fontSize: ".85rem", marginTop: 4 }}>{op.siteNom} · {op.eeNom} · <StatusBadge value={op.criticite} label={labels.criticite[op.criticite]} /> · <span className="mono" data-testid="my-role">{op.monRole}</span></div>
        </div>
      </div>

      <div className="steps" style={{ marginBottom: 16 }}>
        {TABS.map((t, i) => (
          <div key={t} className={`step ${i === tab ? "on" : ""}`} style={{ cursor: "pointer", minWidth: 110 }} onClick={() => setTab(i)} data-testid={`optab-${i}`}>{t}</div>
        ))}
      </div>

      {tab === 0 && (
        <div data-testid="op-synthese">
          <DecisionIntervention operationId={id} canEdit={can("edit_operation")} onGoto={setTab} onChanged={() => { load(); setCohKey((k) => k + 1); }} />
          <QualificationPanel operationId={id} canEdit={can("edit_operation")} />
          <DossierIncomplet operationId={id} onGoto={setTab} refreshKey={cohKey} />
          <div className="card">
          <table className="kv"><tbody>
            <tr><td>Site</td><td><b>{op.siteNom}</b></td></tr>
            <tr><td>Donneur d'ordre</td><td>{op.donneurOrdre}</td></tr>
            {op.responsableOperation && <tr><td>Responsable</td><td>{op.responsableOperation}{op.fonctionResponsable ? ` · ${op.fonctionResponsable}` : ""}</td></tr>}
            <tr><td>Entreprise extérieure</td><td>{op.eeNom}</td></tr>
            <tr><td>Sous-traitants</td><td>{op.sousTraitants.map((s) => <span className="tag" key={s}>{s}</span>)}{!op.sousTraitants.length && "—"}</td></tr>
            <tr><td>Période</td><td className="mono">{op.dateDebut} → {op.dateFin}</td></tr>
            <tr><td>Zones concernées</td><td>{op.zones.map((z) => <span className="tag" key={z}>{zoneName(z)}</span>)}</td></tr>
            <tr><td>Nature des travaux</td><td>{op.natureTravaux.map((t) => labels.travaux[t]).join(", ")}</td></tr>
            <tr><td>Criticité</td><td><StatusBadge value={op.criticite} label={labels.criticite[op.criticite]} /></td></tr>
            {op.criticiteJustification && <tr><td>Justification criticité</td><td style={{ fontSize: ".82rem", color: "var(--text-muted)" }}>{op.criticiteJustification}</td></tr>}
            {(op.siteOccupe || op.presencePublicOuCollaborateurs || op.sousTraitance) && <tr><td>Contexte</td><td>{[op.siteOccupe && "Site occupé", op.presencePublicOuCollaborateurs && "Présence public/collaborateurs", op.sousTraitance && "Sous-traitance"].filter(Boolean).map((x) => <span className="tag" key={x}>{x}</span>)}</td></tr>}
            <tr><td>Description</td><td>{op.description || "—"}</td></tr>
          </tbody></table>

          {(op.journalDecisions || []).length > 0 && (
            <>
              <h3 style={{ fontSize: "1rem", margin: "16px 0 8px" }}>Traçabilité des décisions</h3>
              <ul className="journal" data-testid="journal-decisions">
                {op.journalDecisions.slice().reverse().map((j, i) => (
                  <li key={i}><span className="h">{j.h}</span><b>{j.qui}</b> — {j.quoi}{j.base ? <div style={{ fontSize: ".72rem", color: "var(--text-muted)" }}>Base : {j.base}</div> : null}</li>
                ))}
              </ul>
            </>
          )}
          </div>
        </div>
      )}

      {tab === 1 && insp && (
        <div className="card" style={{ maxWidth: 860 }} data-testid="op-inspection">
          <h3 style={{ fontSize: "1rem", marginBottom: 6 }}>Inspection commune préalable</h3>
          <label className="f">Date</label>
          <input type="date" value={insp.date} onChange={(e) => inspField("date", e.target.value)} data-testid="insp-date" />
          <label className="f">Participants (un par ligne)</label>
          <textarea rows={3} data-testid="insp-participants" value={raw.participants} onChange={(e) => setRaw((r) => ({ ...r, participants: e.target.value }))} placeholder="Un participant par ligne" />
          <label className="f">Zones visitées</label>
          <div className="chips">
            {siteZones.map((z) => (
              <button type="button" key={z.id} className={`chip ${insp.zonesVisitees.includes(z.id) ? "on" : ""}`}
                onClick={() => inspField("zonesVisitees", insp.zonesVisitees.includes(z.id) ? insp.zonesVisitees.filter((x) => x !== z.id) : [...insp.zonesVisitees, z.id])}>{z.nom}</button>
            ))}
          </div>
          <div className="row2">
            <div><label className="f">Risques identifiés</label><textarea rows={4} data-testid="insp-risques" value={raw.risques} onChange={(e) => setRaw((r) => ({ ...r, risques: e.target.value }))} placeholder="Un risque par ligne" /></div>
            <div><label className="f">Mesures de prévention</label><textarea rows={4} data-testid="insp-mesures" value={raw.mesures} onChange={(e) => setRaw((r) => ({ ...r, mesures: e.target.value }))} placeholder="Une mesure par ligne" /></div>
          </div>
          <div className="row2">
            <div><label className="f">Documents demandés</label><textarea rows={3} value={raw.documentsDemandes} onChange={(e) => setRaw((r) => ({ ...r, documentsDemandes: e.target.value }))} /></div>
            <div><label className="f">Réserves</label><textarea rows={3} data-testid="insp-reserves" value={raw.reserves} onChange={(e) => setRaw((r) => ({ ...r, reserves: e.target.value }))} /></div>
          </div>
          <label className="f">Validation finale</label>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: ".85rem", width: "auto" }}>
              <input type="checkbox" style={{ width: "auto" }} data-testid="insp-valide" checked={insp.validation.valide}
                onChange={(e) => inspField("validation", { ...insp.validation, valide: e.target.checked, date: e.target.checked ? new Date().toLocaleDateString("fr-FR") : "" })} />
              Inspection validée par le donneur d'ordre
            </label>
            <input placeholder="Nom du valideur" style={{ maxWidth: 220 }} value={insp.validation.valideur} onChange={(e) => inspField("validation", { ...insp.validation, valideur: e.target.value })} />
          </div>
          <div style={{ marginTop: 16 }}>{can("edit_operation")
            ? <button className="btn btn-p" onClick={saveInspection} data-testid="insp-save">Enregistrer l'inspection</button>
            : <div className="notice" data-testid="insp-readonly">Lecture seule : votre rôle ne permet pas de modifier l'inspection commune.</div>}</div>
        </div>
      )}

      {tab === 2 && (
        <div data-testid="op-reglementaire">
          <AnalyseRisques operationId={id} op={op} canEdit={can("edit_operation")} onChange={() => { load(); setCohKey((k) => k + 1); }} />
          <div className="notice" style={{ marginTop: 12 }}>Assistant réglementaire : chaque activité déclenche ses obligations, questions, justificatifs et mesures possibles. Le moteur ne conclut pas tant que les informations obligatoires ne sont pas connues. Aucune mesure sans justification ni référence.</div>
          <QuestionnaireReglementaire operationId={id} initialAnswers={op.questionnaire || {}} onUpdate={() => setCohKey((k) => k + 1)} />
        </div>
      )}

      {tab === 3 && (
        <div data-testid="op-pdp">
          <CoherencePanel key={cohKey} operationId={id} onChange={() => setCohKey((k) => k + 1)} onGoto={setTab} onState={handleCohState} />
          {can("edit_operation") && (
            <details style={{ margin: "12px 0" }} data-testid="pdp-complements-toggle">
              <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: ".9rem", color: "var(--sarcelle)" }}>
                Compléments documentaires du plan (identification, phases, secours, annexes…)
              </summary>
              <div style={{ marginTop: 10 }}><PdpComplements op={op} onSaved={load} /></div>
            </details>
          )}
          <div style={{ display: "flex", gap: 10, margin: "14px 0", flexWrap: "wrap", alignItems: "center" }}>
            {can("edit_operation") && <button className="btn btn-p" onClick={genPdp} disabled={cohBlocked && !generated} data-testid="pdp-generate"
              title={cohBlocked && !generated ? "Levez d'abord les points bloquants du contrôle de cohérence ci-dessus" : ""}><RefreshCw size={14} />{generated ? "Régénérer le plan" : "Générer le plan de prévention"}</button>}
            {can("edit_operation") && cohBlocked && !generated && (
              <span data-testid="pdp-generate-hint" style={{ fontSize: ".8rem", color: "var(--rouge)" }}>
                Génération désactivée : {cohCount} point{cohCount > 1 ? "s" : ""} bloquant{cohCount > 1 ? "s" : ""} à corriger ci-dessus.
              </span>
            )}
            {generated && <>
              <a className="btn btn-g" href={`${API}/operations/${id}/pdp/pdf`} target="_blank" rel="noreferrer" data-testid="pdp-pdf"><Download size={14} />Exporter PDF</a>
              <a className="btn btn-g" href={`${API}/operations/${id}/pdp/docx`} target="_blank" rel="noreferrer" data-testid="pdp-docx"><FileDown size={14} />Exporter Word</a>
              <button className="btn btn-g" onClick={() => window.open(`/pdp/${id}/print`, "_blank")} data-testid="pdp-print"><Printer size={14} />Page imprimable</button>
            </>}
          </div>
          {generated ? <>
            <SignatureCircuit pdp={pdp} operationId={id} onSigned={load} />
            <PdpView pdp={pdp} />
          </> : <div className="card"><FileText size={20} /> <span style={{ marginLeft: 8 }}>Aucun plan de prévention généré. La génération est conditionnée au contrôle de cohérence ci-dessus : elle est refusée tant qu'il manque des informations obligatoires.</span></div>}
        </div>
      )}

      {tab === 4 && (
        <div data-testid="op-autos">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button className="btn btn-p" onClick={() => nav(`/autorisations/new?op=${id}`)} data-testid="op-new-auto"><Plus size={14} />Autorisation de travail</button>
          </div>
          <div className="tablewrap"><table>
            <thead><tr><th>Réf.</th><th>Zone</th><th>Horaires</th><th>Travaux</th><th>Statut</th></tr></thead>
            <tbody>
              {op.autorisations.length ? op.autorisations.map((a) => (
                <tr className="click" key={a.id} onClick={() => nav(`/autorisations/${a.id}`)} data-testid={`op-auto-${a.id}`}>
                  <td className="mono">{a.id}</td><td>{zoneName(a.zone)}</td><td className="mono">{a.deb}–{a.fin}</td>
                  <td style={{ fontSize: ".78rem" }}>{a.typesTravail.map((t) => labels.travaux[t]).join(", ")}</td>
                  <td><StatusBadge value={a.statut} label={labels.statutsAt[a.statut]} /></td>
                </tr>
              )) : <tr><td colSpan={5} className="empty">Aucune autorisation rattachée.</td></tr>}
            </tbody>
          </table></div>
        </div>
      )}

      {tab === 5 && (
        <div data-testid="op-actions">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button className="btn btn-g" onClick={genActions} data-testid="op-gen-actions"><RefreshCw size={14} />Générer les actions</button>
          </div>
          <div className="tablewrap"><table>
            <thead><tr><th>Intitulé</th><th>Origine</th><th>Responsable</th><th>Échéance</th><th>Priorité</th><th>Statut</th></tr></thead>
            <tbody>
              {op.actions.length ? op.actions.map((a) => (
                <tr key={a.id} data-testid={`op-action-${a.id}`}>
                  <td>{a.intitule}</td><td className="mono" style={{ fontSize: ".72rem" }}>{a.origine}</td><td>{a.responsable}</td>
                  <td className="mono">{a.echeance}</td><td><StatusBadge value={a.priorite} label={labels.priorite[a.priorite]} /></td>
                  <td><StatusBadge value={a.statut} label={labels.statutAction[a.statut]} /></td>
                </tr>
              )) : <tr><td colSpan={6} className="empty">Aucune action.</td></tr>}
            </tbody>
          </table></div>
        </div>
      )}

      {tab === 6 && (
        <div data-testid="op-equipe">
          <ParticipantsPanel operationId={id} canManage={can("manage_participants")} entreprises={entreprises} />
        </div>
      )}

      {tab === 7 && (
        <div data-testid="op-journal">
          <JournalPanel operationId={id} canRead={can("read_audit")} refreshKey={journalKey} />
        </div>
      )}

    </div>
  );
}

function PdpView({ pdp }) {
  const S = ({ n, t, children }) => (
    <div className="card"><h3 style={{ fontSize: "1rem", color: "var(--sarcelle)", marginBottom: 8 }}>{n}. {t}</h3>{children}</div>
  );
  const UL = ({ items }) => items.length ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: ".86rem", lineHeight: 1.6 }}>{items.map((x, i) => <li key={i}>{x}</li>)}</ul> : <div className="empty" style={{ textAlign: "left", padding: 0 }}>—</div>;
  return (
    <div>
      <div className="card" style={{ borderLeft: "4px solid var(--sarcelle)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div><b>{pdp.operation.titre}</b> · <span className="mono">{pdp.ref} v{pdp.version}</span></div>
          <StatusBadge value={pdp.statut} label={pdp.statutLabel} />
        </div>
      </div>
      <S n={1} t="Contexte de l'opération"><p style={{ fontSize: ".86rem" }}>{pdp.contexte || "—"}</p></S>
      <S n={2} t="Entreprises concernées"><UL items={pdp.entreprises} /></S>
      <div className="grid2">
        <S n={3} t="Risques liés aux activités"><UL items={pdp.risquesActivites} /></S>
        <S n={4} t="Risques liés aux lieux"><UL items={pdp.risquesLieux} /></S>
      </div>
      <S n={5} t="Risques de coactivité">
        {pdp.risquesCoactivite.length ? pdp.risquesCoactivite.map((r, i) => (
          <div key={i} className="conf" style={{ marginBottom: 8 }}><div className="t"><span className="mono">{r.code}</span></div><div className="d">{r.msg}</div>{r.fix && <div className="fix">→ {r.fix}</div>}</div>
        )) : <div className="empty" style={{ textAlign: "left", padding: 0 }}>Aucune incompatibilité détectée.</div>}
      </S>
      <S n={6} t="Mesures de prévention (justifiées)">
        {pdp.mesuresJustifiees && pdp.mesuresJustifiees.length ? pdp.mesuresJustifiees.map((m, i) => (
          <div key={i} className="vitem" style={{ borderLeft: "3px solid var(--sarcelle)", paddingLeft: 10 }}>
            <b style={{ fontSize: ".86rem" }}>{m.label}</b>
            <div style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>Pourquoi : {m.justification}{m.motif ? ` — Motif : « ${m.motif} »` : ""}</div>
            <div style={{ fontSize: ".72rem", marginTop: 2 }}><span className="tag">{m.reference?.code}</span> {m.reference?.libelle}</div>
          </div>
        )) : <UL items={pdp.mesures} />}
      </S>
      <S n={7} t="Consignes spécifiques"><UL items={pdp.consignes} /></S>
      <S n={8} t="Responsabilités"><UL items={pdp.responsabilites.map((r) => `${r.role} : ${r.nom}`)} /></S>
      <S n={9} t="Validations">
        <table><thead><tr><th>Étape</th><th>Responsable</th><th>Statut</th></tr></thead>
          <tbody>{pdp.validations.map((v, i) => <tr key={i}><td>{v.etape}</td><td>{v.nom}</td><td>{v.statut}</td></tr>)}</tbody></table>
      </S>
    </div>
  );
}
